import React, { memo, useMemo, useState, useEffect } from 'react';
import { capitalize, groupBy } from 'lodash';
import { ListItemText, ListItem, Typography, Box, Button, Grid, CircularProgress } from '@material-ui/core';
import { ChatBubble, Forum, PhoneIphone as PhoneIcon, StayPrimaryPortrait, ChevronRight } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { VolunteerCommonProfileEmail } from '../../__generated__/VolunteerCommonProfileEmail';
import { VolunteerCommonProfileSms } from '../../__generated__/VolunteerCommonProfileSms';
import EmailBouncedIcon from '../../../../../assets/icons/emailBouncedIcon.svg';
import EmailClickedIcon from '../../../../../assets/icons/emailClickedIcon.svg';
import EmailDeliveredIcon from '../../../../../assets/icons/emailDeliveredIcon.svg';
import EmailOpenedIcon from '../../../../../assets/icons/emailOpenedIcon.svg';
import EmailSentIcon from '../../../../../assets/icons/emailSentIcon.svg';
import EmailIcon from '../../../../../assets/icons/emailIcon.svg';
import { CommunicationViewMoreDialog } from './CommunicationViewMoreDialog';
import { CommunicationDetailDialog } from './CommunicationDetailDialog';
import { SmsLogTypeParsed } from '../../VolunteerCommonProfileDialog';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { OutboundSmsListItem } from './OutboundSmsListItem';
import { ApplicationReminderCommunication, ApplicationReminderListItem } from './ApplicationReminderListItem';
import { useCampfireLazyQuery } from '../../../../../global/network/useCampfireLazyQuery';
import { GET_VOLUNTEER_COMMUNICATIONS } from '../../communications-model.gql';
import {
  GetVolunteerCommunications,
  GetVolunteerCommunicationsVariables,
} from '../../__generated__/GetVolunteerCommunications';

const EmailListItem = ({
  email,
  type,
  iconType,
  color,
  onDetailsClick,
}: {
  email: VolunteerCommonProfileEmail;
  type: string;
  iconType: string;
  color: string;
  onDetailsClick: () => void;
}) => (
  <Box display='flex' justifyContent='flex-between' key={email.postmarkEmailId}>
    <Box mt='28px' width='85px' position='relative'>
      <Box
        style={{
          display: 'flex',
          backgroundColor: `${color}`,
          borderRadius: '3px',
          padding: '4px 8px',
          alignItems: 'center',
          position: 'absolute',
          right: 0,
        }}
      >
        <img src={iconType} alt='' style={{ marginRight: '4px' }} />
        <Typography style={{ fontWeight: 600, fontSize: '11px', color: '#FCFCFC' }}>{type}</Typography>
      </Box>
    </Box>
    <ListItem style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <ListItemText
        primary={
          <Typography variant='h6' style={{ fontWeight: 900, fontSize: '14px', color: '#444444' }}>
            {email.subject}
          </Typography>
        }
        secondary={
          <Typography style={{ fontSize: '10px', color: '#9E9E9E', fontStyle: 'italic' }}>
            {email.sentAt &&
              `Sent ${unpackToDateTime(email.sentAt).toFormat('h:mm a, dd LLLL yyyy')}${
                email.sentBy ? ` by ${email.sentBy}` : ''
              }`}
          </Typography>
        }
      />
      <Box display='flex'>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#d9d9d9',
            borderRadius: '3px',
            padding: '4px 8px',
            marginRight: '10px',
          }}
        >
          <img src={EmailIcon} alt='' style={{ marginRight: '4px', marginBottom: '0.5px' }} />
          <Typography style={{ fontWeight: 600, fontSize: '10px', color: '#545454' }}>{`Email`}</Typography>
        </Box>
        {email.communicationTemplate && (
          <Box style={{ border: '1px solid #d9d9d9', borderRadius: '3px', padding: '4px 8px' }}>
            <Typography style={{ fontWeight: 600, fontSize: '10px', color: '#545454' }}>
              {email.communicationTemplate.name}
            </Typography>
          </Box>
        )}
      </Box>
    </ListItem>
    <Box display='flex' alignItems='center' ml={1}>
      <Button
        onClick={onDetailsClick}
        style={{
          minWidth: 'auto',
          padding: '4px',
          color: '#9E9E9E',
        }}
      >
        <ChevronRight />
      </Button>
    </Box>
  </Box>
);

const SmsListItem = ({ sms, onDetailsClick }: { sms: SmsLogTypeParsed; onDetailsClick: () => void }) => {
  const { theme } = useCampfireTheme();

  return (
    <Box display='flex' justifyContent='flex-between' key={sms.smsLogId}>
      <Box display='flex' justifyContent='flex-between'>
        <Box mt='28px' width='85px' position='relative'>
          <Box
            style={{
              display: 'flex',
              backgroundColor: sms.status === 'replied' ? theme.color.secondary.main900 : '#83C4A0',
              borderRadius: '3px',
              padding: '4px 8px',
              alignItems: 'center',
              position: 'absolute',
              right: 0,
            }}
          >
            {sms.status === 'replied' ? (
              <Box display='flex' alignItems='flex-start' mr='4px'>
                <StayPrimaryPortrait style={{ width: '10px', height: '10px', color: '#ffffff' }} />
                <ChatBubble style={{ width: '10px', height: '7px', color: '#ffffff', marginLeft: '-2px' }} />
              </Box>
            ) : (
              <PhoneIcon style={{ width: '10px', height: '10px', color: '#ffffff', marginRight: '4px' }} />
            )}
            <Typography style={{ fontWeight: 600, fontSize: '11px', color: '#ffffff' }}>
              {capitalize(sms.status)}
            </Typography>
          </Box>
        </Box>
        <ListItem style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <ListItemText
            primary={
              <Typography variant='h6' style={{ fontWeight: 900, fontSize: '14px', color: '#444444' }}>
                SMS Volunteer Activity Attendance Confirmation
              </Typography>
            }
            secondary={
              <Typography style={{ fontSize: '10px', color: '#9E9E9E', fontStyle: 'italic' }}>
                {`${capitalize(sms.status)} ${unpackToDateTime(sms.sentAt).toFormat('h:mm a, dd LLLL yyyy')}`}
              </Typography>
            }
          />
          <Box display='flex'>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#d9d9d9',
                borderRadius: '3px',
                padding: '4px 8px',
                marginRight: '10px',
              }}
            >
              <PhoneIcon style={{ width: '10px', height: '10px', color: '#545454' }} />
              <Typography style={{ fontWeight: 600, fontSize: '10px', color: '#545454' }}>SMS</Typography>
            </Box>
          </Box>
        </ListItem>
      </Box>
      <Box display='flex' alignItems='center' ml={1}>
        <Button
          onClick={onDetailsClick}
          style={{
            minWidth: 'auto',
            padding: '4px',
            color: '#9E9E9E',
          }}
        >
          <ChevronRight />
        </Button>
      </Box>
    </Box>
  );
};

export const VolunteerCommunicationParsed = memo(
  ({
    communication,
    onDetailsClick,
  }: {
    communication:
      | VolunteerCommonProfileEmail
      | SmsLogTypeParsed
      | VolunteerCommonProfileSms
      | ApplicationReminderCommunication;
    onDetailsClick: () => void;
  }) => {
    return (
      <>
        {communication && communication.__typename === 'VOLUNTEER_ApplicationReminderType' ? (
          <ApplicationReminderListItem reminder={communication} />
        ) : communication && communication.__typename === 'EMAIL_PostmarkOutboundEmailType' ? (
          <>
            {communication.postmarkBounce ? (
              <EmailListItem
                email={communication}
                type='Bounced'
                iconType={EmailBouncedIcon}
                color='#D93A00'
                onDetailsClick={onDetailsClick}
              />
            ) : communication.postmarkClicks.length > 0 ? (
              communication.postmarkClicks.map(() => (
                <EmailListItem
                  email={communication}
                  type='Clicked'
                  iconType={EmailClickedIcon}
                  color='#7570EA'
                  onDetailsClick={onDetailsClick}
                />
              ))
            ) : communication.postmarkOpens.length > 0 ? (
              communication.postmarkOpens.map(() => (
                <EmailListItem
                  email={communication}
                  type='Opened'
                  iconType={EmailOpenedIcon}
                  color='#2FC0F9'
                  onDetailsClick={onDetailsClick}
                />
              ))
            ) : communication.postmarkDelivery ? (
              <EmailListItem
                email={communication}
                type='Delivered'
                iconType={EmailDeliveredIcon}
                color='#48976C'
                onDetailsClick={onDetailsClick}
              />
            ) : communication ? (
              <EmailListItem
                email={communication}
                type='Sent'
                iconType={EmailSentIcon}
                color='#83C4A0'
                onDetailsClick={onDetailsClick}
              />
            ) : (
              ''
            )}
          </>
        ) : communication.__typename === 'EMAIL_OutboundSmsType' ? (
          <OutboundSmsListItem sms={communication} onDetailsClick={onDetailsClick} />
        ) : (
          <SmsListItem sms={communication} onDetailsClick={onDetailsClick} />
        )}
      </>
    );
  }
);

const parseSmsLogs = (data: GetVolunteerCommunications): Array<SmsLogTypeParsed> => {
  const rosterSmsLogs = data.vm.volunteer?.rosterings.flatMap((rostering) => rostering.smsLogs) ?? [];
  return Object.values(groupBy(rosterSmsLogs, 'messageId')).map((item) => {
    const reply = item.find((i) => i.direction);
    if (item.length > 1 && reply) {
      return { ...reply, status: 'replied' };
    }
    return { ...item[0], status: 'sent' };
  });
};

export const VolunteerCommunicationsSection = memo(() => {
  const LIMIT = 10;
  const [viewMore, setViewMore] = useState(false);
  const [emails, setEmails] = useState<Array<VolunteerCommonProfileEmail>>([]);
  const [outboundSms, setOutboundSms] = useState<Array<VolunteerCommonProfileSms>>([]);
  const [smsLogs, setSmsLogs] = useState<Array<SmsLogTypeParsed>>([]);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    communication: VolunteerCommonProfileEmail | VolunteerCommonProfileSms | SmsLogTypeParsed | null;
  }>({ open: false, communication: null });

  const {
    volunteerNameOptions: { preferredName },
    userId,
    profileId,
  } = useVolunteerCommonProfileContext();

  const [getCommunications] = useCampfireLazyQuery<GetVolunteerCommunications, GetVolunteerCommunicationsVariables>(
    GET_VOLUNTEER_COMMUNICATIONS
  );

  useEffect(() => {
    if (!userId || !profileId) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    getCommunications({
      variables: { userId, profileId },
    })
      .then(({ data }) => {
        if (!active) return;
        if (data) {
          setEmails(data.em.emails ?? []);
          setOutboundSms(data.em.outboundSms ?? []);
          setSmsLogs(parseSmsLogs(data));
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [userId, profileId, getCommunications]);

  const communications = useMemo(() => {
    return [...emails, ...outboundSms, ...smsLogs].sort((a, b) => {
      if (!b.sentAt) {
        return a.sentAt ? -1 : 1;
      }
      return unpackToDateTime(a.sentAt) > unpackToDateTime(b.sentAt) ? -1 : 1;
    });
  }, [emails, outboundSms, smsLogs]);

  const onViewMore = () => {
    setViewMore(true);
  };

  const closeViewMore = () => {
    setViewMore(false);
  };

  const openDetailDialog = (
    communication: VolunteerCommonProfileEmail | VolunteerCommonProfileSms | SmsLogTypeParsed
  ) => {
    setDetailDialog({ open: true, communication });
  };

  const closeDetailDialog = () => {
    setDetailDialog({ open: false, communication: null });
  };

  return (
    <Grid container spacing={2} direction='column' style={{ position: 'relative' }}>
      <Grid item xs={12} style={{ textAlign: 'left' }}>
        <Typography style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}>
          <Forum style={{ fontSize: '14px', paddingRight: '5px' }} /> Communications
        </Typography>
        <Typography variant='body2' style={{ color: '#464646' }} align='left'>
          Communications that have been sent to and received from {preferredName}
          <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />
        </Typography>
        {loading && (
          <Box display='flex' justifyContent='center' padding='2rem 0'>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading &&
          communications
            .slice(0, LIMIT)
            .map((communication) => (
              <VolunteerCommunicationParsed
                key={
                  communication.__typename === 'EMAIL_PostmarkOutboundEmailType'
                    ? communication.postmarkEmailId
                    : communication.__typename === 'EMAIL_OutboundSmsType'
                    ? communication.outboundSmsId
                    : communication.smsLogId
                }
                communication={communication}
                onDetailsClick={() => openDetailDialog(communication)}
              />
            ))}
        {!loading && communications.length > 10 && (
          <Box display='flex' justifyContent='center'>
            <Button
              onClick={onViewMore}
              disableElevation
              color='primary'
              disabled={false}
              size='medium'
              endIcon={<ExpandMoreIcon />}
              style={{
                alignSelf: 'center',
                marginTop: '1rem',
                color: '#545454',
                padding: '2px 6px',
                fontSize: '12px',
                fontWeight: 400,
                borderRadius: '6px',
                textTransform: 'capitalize',
              }}
            >
              View more
            </Button>
          </Box>
        )}
        {viewMore && (
          <CommunicationViewMoreDialog
            open
            handleClose={closeViewMore}
            communications={communications}
            subtitle={`Communications that have been sent to ${preferredName}`}
            onDetailsClick={(communication) => setDetailDialog({ open: true, communication })}
          />
        )}
      </Grid>
      {!loading && communications.length === 0 && (
        <Typography
          variant='body2'
          style={{ fontSize: '13px', color: '#494949', textAlign: 'center', padding: '2rem 0' }}
        >
          No results found
        </Typography>
      )}

      <CommunicationDetailDialog
        open={detailDialog.open}
        onClose={closeDetailDialog}
        communication={detailDialog.communication}
      />
    </Grid>
  );
});
